import { Component, ErrorHandler, HostListener, Inject, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, afterNextRender, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import {
  AlvMaaritys,
  Kirjanpitotili,
  KirjanpitotilinAlvTyyppi,
  LokalisoidutAlvtyypit,
  LokalisoituKirjanpitotilinAlvTyyppi,
  MonikielinenTeksti,
  MyyntiAlvt,
  OstoAlvt,
} from 'app/_jaettu-lemonator/model/kirjanpito'

import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { Observable, Subject, firstValueFrom } from 'rxjs'
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators'
import { FlatKirjanpitotili } from './asiakkaan-tilikartta-edit.component'

export interface AsiakkaanKirjanpitotiliMuokkaaDialogData {
  tili: Kirjanpitotili
  asiakas: Asiakas
  lisaa: boolean
  tilit: FlatKirjanpitotili[]
}

interface KirjanpitotiliLomake {
  vanhempi: string | null
  numero: string | null
  nimiFi: string
  nimiEn: string
  alv: AlvMaaritys | null
  alvtyyppi: KirjanpitotilinAlvTyyppi | null
  aktiivinen: boolean | null
  reskontraActive: boolean | null
  automaattinenPoisto: boolean
  automaattinenPoistoProsentti: number | null
  automaattinenPoistoTili: Kirjanpitotili | null
}

@Component({
  templateUrl: './kirjanpitotili-muokkaa.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanKirjanpitotiliMuokkaaDialog implements OnInit, OnDestroy {
  @ViewChild('nimiFiInput', { read: MatInput, static: true }) nimiFiInput: MatInput
  @ViewChild('numeroInput', { read: MatInput, static: true }) numeroInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _alvPoissa = signal(false)
  private readonly _poistotPoissa = signal(false)

  commonError: string = ''
  lomake: FieldTree<KirjanpitotiliLomake>
  lisaa = false
  alvt: AlvMaaritys[] = []
  lokalisoidutAlvtyypit: LokalisoituKirjanpitotilinAlvTyyppi[] = LokalisoidutAlvtyypit.kaikki
  tilitObservable: Observable<Kirjanpitotili[]>
  suodatetutTilitSubject: Subject<FlatKirjanpitotili[]> = new Subject<[]>()

  vertaaAlvMaarityksia: (a: AlvMaaritys, b: AlvMaaritys) => boolean = (a: AlvMaaritys, b: AlvMaaritys): boolean => {
    return a && b && a.tunniste === b.tunniste
  }

  private asetaAlvt(tyyppi: KirjanpitotilinAlvTyyppi) {
    if (tyyppi === KirjanpitotilinAlvTyyppi.MYYNTI) {
      this.alvt = [].concat(MyyntiAlvt.kaikki)
    } else if (tyyppi === KirjanpitotilinAlvTyyppi.OSTO) {
      this.alvt = [].concat(OstoAlvt.kaikki)
    } else if (tyyppi === KirjanpitotilinAlvTyyppi.ETAMYYNTI_EI_REKISTEROITYNYT) {
      this.alvt = [].concat(MyyntiAlvt.suomi)
    } else {
      this.alvt = []
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: AsiakkaanKirjanpitotiliMuokkaaDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanKirjanpitotiliMuokkaaDialog, boolean>,
    private _errorHandler: ErrorHandler,
    private _tilikarttaService: TilikarttaService,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _dialog: MatDialog,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {
    this.lisaa = this._data.lisaa
    this.tilitObservable = this._tilikarttaService.profiilinPaatilikartanTilitObservable.pipe(
      map(tilit => {
        if (tilit) {
          return tilit.filter(t => t.aktiivinen && t.numero.length === 4)
        }
        return []
      }),
    )
  }

  ngOnInit() {
    const kaytettavaTyyppi = this._data.tili.alvTyyppi || KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA
    const onEiAlvKasittelya = kaytettavaTyyppi === KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA || kaytettavaTyyppi === KirjanpitotilinAlvTyyppi.ETAMYYNTI

    this.asetaAlvt(kaytettavaTyyppi)

    this._alvPoissa.set(onEiAlvKasittelya)
    this.lomake = form(
      signal<KirjanpitotiliLomake>({
        vanhempi: this._data.tili.vanhempi ?? null,
        numero: this._data.tili.numero ?? null,
        nimiFi: this._data.tili.localisedName?.fi ?? this._data.tili.nimi ?? '',
        nimiEn: this._data.tili.localisedName?.en ?? '',
        alv: onEiAlvKasittelya ? null : (this._data.tili.oletusAlvKasittely ?? null),
        alvtyyppi: kaytettavaTyyppi,
        aktiivinen: this._data.tili.aktiivinen ?? null,
        reskontraActive: this._data.tili.reskontraActive ?? null,
        automaattinenPoisto: !!this._data.tili.poistotiedot,
        automaattinenPoistoProsentti: this._data.tili?.poistotiedot?.poistoprosentti || 25,
        automaattinenPoistoTili: this._data.tilit.find(tili => tili.kirjanpitotili.numero === this._data.tili?.poistotiedot?.poistotili)?.kirjanpitotili || null,
      }),
      polku => {
        required(polku.vanhempi)
        if (!this._data.lisaa) {
          disabled(polku.numero)
        }
        required(polku.numero)
        validoiReaktiivisesti(polku.numero, Validators.min(10000))
        validoiReaktiivisesti(polku.numero, Validators.max(99999))
        validoiReaktiivisesti(polku.numero, Validators.minLength(5))
        validoiReaktiivisesti(polku.numero, Validators.maxLength(5))
        required(polku.nimiFi)
        required(polku.nimiEn)
        disabled(polku.alv, () => this._alvPoissa())
        required(polku.alv, { when: () => !this._alvPoissa() })
        required(polku.alvtyyppi)
        disabled(polku.automaattinenPoistoProsentti, () => this._poistotPoissa())
        required(polku.automaattinenPoistoProsentti)
        validoiReaktiivisesti(polku.automaattinenPoistoProsentti, Validators.min(0))
        validoiReaktiivisesti(polku.automaattinenPoistoProsentti, Validators.max(100))
        disabled(polku.automaattinenPoistoTili, () => this._poistotPoissa())
        required(polku.automaattinenPoistoTili)
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.alvtyyppi).subscribe(value => {
      asetaArvo(this.lomake.alv, null)
      this.asetaAlvt(value)
      if (value && value !== KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA && value !== KirjanpitotilinAlvTyyppi.ETAMYYNTI) {
        this._alvPoissa.set(false)
      } else {
        this._alvPoissa.set(true)
      }
    })

    // Signal Forms asettaa kentän käytöstilan ensimmäisessä muutostunnistuksessa, mikä nollaa Materialin kentän kohdistustilan
    afterNextRender(
      () => {
        if (this.lisaa) {
          this.numeroInput.focus()
        } else {
          this.nimiFiInput.focus()
        }
      },
      { injector: this._injector },
    )

    muutokset(this.lomake.reskontraActive)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(async isActive => {
        if (
          this._data.tili.reskontraActive && // Was active before
          !isActive // But was just marked as NOT active
        ) {
          const dialogData: AreYouSureDialogData = {
            text: 'Jos reskontra laitetaan pois päältä, kaikki reskontramerkinnät poistetaan.',
            rightAction: 'Kyllä',
          }
          const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())

          if (!isSure) {
            asetaArvo(this.lomake.reskontraActive, true)
          }
        }
      })

    muutokset(this.lomake.automaattinenPoisto)
      .pipe(startWith(this.lomake.automaattinenPoisto().value()), takeUntil(this._ngUnsubscribe))
      .subscribe(automaattinenPoisto => {
        if (automaattinenPoisto) {
          this._asetaPoistotPoissa(false)
        } else {
          this._asetaPoistotPoissa(true)
        }
      })

    muutokset(this.lomake.automaattinenPoistoTili)
      .pipe(debounceTime(50), startWith(''), takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        const suodatetut =
          typeof value === 'string'
            ? this._data.tilit.filter(tili => tili.kirjanpitotili.nimi.toLowerCase().indexOf(value) === 0 || tili.kirjanpitotili.numero.toLowerCase().indexOf(value) === 0)
            : this._data.tilit
        this.suodatetutTilitSubject.next(suodatetut)
      })
  }

  @HostListener('document:keydown.esc')
  peruuta() {
    this._dialogRef.close(false)
  }

  private _asetaPoistotPoissa(poissa: boolean) {
    this._poistotPoissa.set(poissa)
    // Käytöstilan vaihto lähettää poistotilin arvon tilaajille
    asetaArvo(this.lomake.automaattinenPoistoTili, this.lomake.automaattinenPoistoTili().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string) {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  displayPoistotili(tili: Kirjanpitotili): string {
    return tili && tili.nimi && tili.numero ? `${tili.numero} ${tili.nimi}` : ''
  }

  @HostListener('document:keydown.enter')
  async tallenna() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    const localisedName: MonikielinenTeksti = {
      fi: null,
      en: null,
    }

    if (this.lomake.nimiFi().value()) {
      localisedName.fi = this.lomake.nimiFi().value()
    }
    if (this.lomake.nimiEn().value()) {
      localisedName.en = this.lomake.nimiEn().value()
    }

    const tili: Kirjanpitotili = {
      vanhempi: this.lomake.vanhempi().value() ? this.lomake.vanhempi().value() + '' : null,
      aktiivinen: !!this.lomake.aktiivinen().value(),
      numero: this.lomake.numero().value() ? this.lomake.numero().value() + '' : null,
      nimi: this.lomake.nimiFi().value(),
      oletusAlvKasittely: this.lomake.alv().value(),
      alvTyyppi: this.lomake.alvtyyppi().value(),
      localisedName: localisedName,
    }

    if (this.lomake.automaattinenPoisto().value()) {
      tili.poistotiedot = {
        poistoprosentti: this.lomake.automaattinenPoistoProsentti().value(),
        poistotili: this.lomake.automaattinenPoistoTili().value().numero,
      }
    }

    if (!this.lomake.reskontraActive().disabled()) {
      tili.reskontraActive = this.lomake.reskontraActive().value() ?? false
    }

    const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    this._tilikarttaService
      .tallennaAsiakkaanTilikartanTili(this._data.asiakas, tili, kirjanpitajanTiedot.uid)
      .then(() => {
        this._ladataanService.lopetaLataaminen()
        this._dialogRef.close(true)
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError = 'Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.'
        this._errorHandler.handleError(error)
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
